/**
 * What the Password Recovery page does with the server's reply to a lost-password request
 * (ClickUp 86e06umb3).
 *
 * The page says "Email Sent!" whether or not the address is known, so nobody can use it to
 * find out who has an account. The one exception is a refusal: when every company the person
 * belongs to is locked or inactive, the server sends no password and replies with an error
 * telling them to contact support. Only those two messageIds are shown; every other reply,
 * every other error and no reply at all still end on "Email Sent!".
 */
export default class PasswordRecoveryReply {

  static Outcomes = {
    DONE: 'done',
    REFUSED: 'refused',
  };

  // AccountLockedId is HTTP 401 (billing), CompanyNotActiveId is 404.
  static RefusalIds = ['AccountLockedId', 'CompanyNotActiveId'];

  // Long enough for a slow server; after it the page moves on as it always has.
  static TIMEOUT_MS = 10000;

  /**
   * @param reply the raw reply event (with an optional `error` of {messageId, hint}),
   *              or null when none arrived in time
   * @param fallback the text to show for a refusal that came without a hint
   * @returns {{outcome: string, message: string|null}}
   */
  static classify(reply, fallback) {
    const error = reply && reply.error;
    if (error && PasswordRecoveryReply.RefusalIds.includes(error.messageId)) {
      const hint = typeof error.hint === 'string' ? error.hint.trim() : '';
      return { outcome: PasswordRecoveryReply.Outcomes.REFUSED, message: hint || fallback };
    }
    return { outcome: PasswordRecoveryReply.Outcomes.DONE, message: null };
  }

  // A North American phone number - 226-753-8131, (226) 753-8131, 1-800-555-0199, 226.753.8131.
  static PHONE = /(?:\+?1[-. ])?(?:\(\d{3}\) ?|\d{3}[-. ])\d{3}[-. ]\d{4}/g;

  /**
   * Splits a message into runs for the page to draw, marking each phone number so it is kept on
   * one line: the browser otherwise breaks "226-753-8131" at a hyphen. The text itself is not
   * changed, and the parts are drawn as text, never as HTML, because the hint comes from the server.
   *
   * @returns {{text: string, keep: boolean}[]} joined back together, exactly the message
   */
  static messageParts(message) {
    const text = message == null ? '' : String(message);
    const parts = [];
    let last = 0;
    for (const match of text.matchAll(PasswordRecoveryReply.PHONE)) {
      if (match.index > last) parts.push({ text: text.slice(last, match.index), keep: false });
      parts.push({ text: match[0], keep: true });
      last = match.index + match[0].length;
    }
    if (last < text.length) parts.push({ text: text.slice(last), keep: false });
    return parts;
  }

  /**
   * Waits for the reply to one request, keyed by the request's '@rid' in the events store.
   * Resolves with the complete reply, or null after timeoutMs. Intermediate replies keep
   * the listener waiting.
   *
   * @param addListener (eventRid, listener) => void; listener.emit(event) returns true to keep listening
   * @param removeListener (eventRid) => void
   */
  static waitFor(eventRid, addListener, removeListener, timeoutMs = PasswordRecoveryReply.TIMEOUT_MS) {
    return new Promise((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        removeListener(eventRid);
        resolve(null);
      }, timeoutMs);

      addListener(eventRid, {
        emit(event) {
          if (settled) return false;
          if (event && event.status === 'intermediate') return true;
          settled = true;
          clearTimeout(timer);
          resolve(event || null);
          return false;
        },
      });
    });
  }
}
